import {supabase} from '../supabase/client';

export const ACCOUNTANT_INVITATIONS_TABLE='accountant_invitations';
export const ACCOUNTANT_ACCESS_TABLE='accountant_business_access';
export const ACCOUNTANT_AUDIT_TABLE='accountant_audit_events';

function clientOrThrow() {
  if (!supabase) throw new Error('Supabase is not configured.');
  return supabase;
}

export function effectiveInvitationStatus(invitation,now=new Date()) {
  if (invitation.status==='pending' && new Date(invitation.expires_at)<=now) return 'expired';
  return invitation.status;
}

async function scopesForInvitations(invitationIds) {
  if (!invitationIds.length) return [];
  const {data,error}=await clientOrThrow().from(ACCOUNTANT_ACCESS_TABLE)
    .select('invitation_id,business_id').in('invitation_id',invitationIds);
  if (error) throw error;
  return data || [];
}

export async function listOwnerAccountants(ownerUserId,businesses=[]) {
  const {data,error}=await clientOrThrow().from(ACCOUNTANT_INVITATIONS_TABLE)
    .select('*').eq('owner_user_id',ownerUserId).order('created_at',{ascending:false});
  if (error) throw error;
  const invitations=data || [];
  const scopes=await scopesForInvitations(invitations.map(item=>item.id));
  const businessMap=new Map(businesses.map(item=>[item.id,item]));
  return invitations.map(invitation=>({
    ...invitation,
    effective_status:effectiveInvitationStatus(invitation),
    businesses:scopes.filter(scope=>scope.invitation_id===invitation.id)
      .map(scope=>businessMap.get(scope.business_id) || {id:scope.business_id,name:'Business'})
  }));
}

export async function listCurrentAccountantAccess(userId) {
  if (!userId) return [];
  const {data,error}=await clientOrThrow().from(ACCOUNTANT_INVITATIONS_TABLE)
    .select('id,owner_user_id,permission_level,status').eq('accountant_user_id',userId).eq('status','active');
  if (error) throw error;
  const invitations=data || [];
  const scopes=await scopesForInvitations(invitations.map(item=>item.id));
  return scopes.map(scope=>{
    const invitation=invitations.find(item=>item.id===scope.invitation_id);
    return {...scope,owner_user_id:invitation.owner_user_id,permission_level:invitation.permission_level};
  });
}

export async function getAccountantCapacity() {
  const {data,error}=await clientOrThrow().rpc('get_accountant_capacity');
  if (error) throw error;
  return data?.[0] || {plan_type:'solo',accountant_limit:1,current_count:0};
}

export async function sendAccountantInvitation(invitationId) {
  const {data,error}=await clientOrThrow().functions.invoke('send-accountant-invitation',{
    body:{invitation_id:invitationId}
  });
  if (error) throw error;
  if (!data?.sent) throw new Error(data?.error || 'The invitation email could not be sent.');
  return data;
}

export async function createAccountantInvitation({email,permissionLevel,businessIds}) {
  const {data,error}=await clientOrThrow().rpc('create_accountant_invitation',{
    p_email:email.trim().toLowerCase(),p_permission_level:permissionLevel,p_business_ids:businessIds
  });
  if (error) throw error;
  let emailError=null;
  try { await sendAccountantInvitation(data.id); } catch (sendError) { emailError=sendError; }
  return {invitation:data,emailError};
}

export async function resendAccountantInvitation(invitationId) {
  const {data,error}=await clientOrThrow().rpc('renew_accountant_invitation',{p_invitation_id:invitationId});
  if (error) throw error;
  await sendAccountantInvitation(invitationId);
  return data;
}

export async function updateAccountantAccess(invitationId,permissionLevel,businessIds) {
  const {data,error}=await clientOrThrow().rpc('update_accountant_access',{
    p_invitation_id:invitationId,p_permission_level:permissionLevel,p_business_ids:businessIds
  });
  if (error) throw error;
  return data;
}

export async function revokeAccountantAccess(invitationId) {
  const {error}=await clientOrThrow().rpc('revoke_accountant_access',{p_invitation_id:invitationId});
  if (error) throw error;
}

export async function acceptAccountantInvitation(invitationId) {
  const {data,error}=await clientOrThrow().rpc('accept_accountant_invitation',{p_invitation_id:invitationId});
  if (error) throw error;
  return data;
}

export async function listAccountantAudit(businessIds) {
  if (!businessIds.length) return [];
  const {data,error}=await clientOrThrow().from(ACCOUNTANT_AUDIT_TABLE)
    .select('*').in('business_id',businessIds).order('created_at',{ascending:false}).limit(30);
  if (error) throw error;
  return data || [];
}

export async function getAccountantReceiptUrl(businessId,expenseId,{download=false}={}) {
  const client=clientOrThrow();
  const {data:path,error:pathError}=await client.rpc('request_accountant_receipt_access',{
    p_business_id:businessId,p_expense_id:expenseId,p_action:download?'download':'view'
  });
  if(pathError) throw pathError;
  const options=download?{download:true}:undefined;
  const {data,error}=await client.storage.from('receipts').createSignedUrl(path,60,options);
  if(error) throw new Error(`Receipt access failed: ${error.message}`);
  if(!data?.signedUrl) throw new Error('Receipt access failed. Please try again.');
  return data.signedUrl;
}

export async function recordAccountantExport(businessId,year,month,eventType) {
  const {error}=await clientOrThrow().rpc('record_accountant_export',{
    p_business_id:businessId,p_year:year,p_month:month,p_event_type:eventType
  });
  if(error) throw error;
}

export async function getAccountantWorkspaceSummary(businessId,date=new Date()) {
  const from=new Date(date.getFullYear(),date.getMonth(),1).toISOString().slice(0,10);
  const to=new Date(date.getFullYear(),date.getMonth()+1,1).toISOString().slice(0,10);
  const [missingResult,missingCountResult,monthlyExpensesResult,incomeResult,accountsResult,reviewResult]=await Promise.all([
    clientOrThrow().from('expenses_1772928000000')
      .select('id,merchant,amount,expense_date,category,status,receipt_url').eq('business_id',businessId)
      .or('receipt_url.is.null,receipt_url.eq.').order('expense_date',{ascending:false}).limit(6),
    clientOrThrow().from('expenses_1772928000000')
      .select('id',{count:'exact',head:true}).eq('business_id',businessId).or('receipt_url.is.null,receipt_url.eq.'),
    clientOrThrow().from('expenses_1772928000000').select('amount').eq('business_id',businessId)
      .gte('expense_date',from).lt('expense_date',to),
    clientOrThrow().from('income_records_1772929000000').select('id,amount,income_date')
      .eq('business_id',businessId).gte('income_date',from).lt('income_date',to),
    clientOrThrow().from('bank_accounts').select('id').eq('business_id',businessId),
    clientOrThrow().from('review_periods_1772928900000').select('year,month,status,reviewed_at')
      .eq('business_id',businessId).order('year',{ascending:false}).order('month',{ascending:false}).limit(1).maybeSingle()
  ]);
  for(const result of [missingResult,missingCountResult,monthlyExpensesResult,incomeResult,accountsResult,reviewResult]) if(result.error) throw result.error;
  const accountIds=(accountsResult.data || []).map(item=>item.id);
  let unresolved=[];
  let unresolvedCount=0;
  if(accountIds.length){
    const [rowsResult,countResult]=await Promise.all([
      clientOrThrow().from('bank_transactions')
        .select('id,transaction_date,description,amount,status,account_id').in('account_id',accountIds)
        .eq('status','unmatched').order('transaction_date',{ascending:false}).limit(6),
      clientOrThrow().from('bank_transactions').select('id',{count:'exact',head:true})
        .in('account_id',accountIds).eq('status','unmatched')
    ]);
    if(rowsResult.error) throw rowsResult.error;
    if(countResult.error) throw countResult.error;
    unresolved=rowsResult.data || [];
    unresolvedCount=countResult.count || 0;
  }
  return {
    period:{from,to},
    expenseTotal:(monthlyExpensesResult.data||[]).reduce((sum,item)=>sum+Number(item.amount||0),0),
    incomeTotal:(incomeResult.data||[]).reduce((sum,item)=>sum+Number(item.amount||0),0),
    missingReceipts:missingResult.data || [],
    missingReceiptCount:missingCountResult.count || 0,
    unresolvedTransactions:unresolved,
    unresolvedTransactionCount:unresolvedCount,
    latestReview:reviewResult.data || null
  };
}


export async function getReviewerMfaState() {
  const client=clientOrThrow();
  const [{data:assurance,error:assuranceError},{data:factors,error:factorsError}]=await Promise.all([
    client.auth.mfa.getAuthenticatorAssuranceLevel(),
    client.auth.mfa.listFactors()
  ]);
  if(assuranceError) throw assuranceError;
  if(factorsError) throw factorsError;
  const verifiedTotp=(factors?.totp || []).filter(factor=>factor.status==='verified');
  return {
    currentLevel:assurance?.currentLevel || 'aal1',
    nextLevel:assurance?.nextLevel || 'aal1',
    verifiedTotp
  };
}

export async function beginReviewerMfaEnrollment() {
  const {data,error}=await clientOrThrow().auth.mfa.enroll({
    factorType:'totp',
    friendlyName:'SilaTrail reviewer'
  });
  if(error) throw error;
  return data;
}

export async function verifyReviewerMfa(factorId,code) {
  const client=clientOrThrow();
  const normalized=String(code || '').replace(/\s/g,'');
  if(!/^\d{6}$/.test(normalized)) throw new Error('Enter the 6-digit code from your authenticator app.');
  const {data:challenge,error:challengeError}=await client.auth.mfa.challenge({factorId});
  if(challengeError) throw challengeError;
  const {data,error}=await client.auth.mfa.verify({
    factorId,
    challengeId:challenge.id,
    code:normalized
  });
  if(error) throw error;
  return data;
}

export async function reviewAccountantExpense(businessId,expenseId,{category,status}) {
  const {data,error}=await clientOrThrow().rpc('review_accountant_expense',{
    p_business_id:businessId,
    p_expense_id:expenseId,
    p_category:category,
    p_status:status
  });
  if(error) throw error;
  return data;
}
